import { Accordion, AccordionDetails, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { Operator } from 'shell/models/operator';
import { HeadCell } from 'shell/components/shared/table/types';
import { OperatorClient } from 'system/view-models/operator-list';
import TableOperator from './table';
import { SystemAuthorizations } from 'shell/authorizations/system';

export const headCells: HeadCell<OperatorClient, keyof OperatorClient>[] = [
  {
    id: 'name',
    label: 'Nome',
  },
  {
    id: 'action',
    label: '',
  },
];

interface ListOperatorProps {
  qualificationId: string;
  isEdit?: boolean;
  onDetailPress?: (id: string) => void;
  onEditPress?: (id: string) => void;
}

const ListOperator = ({
  qualificationId,
  isEdit,
  onDetailPress,
  onEditPress,
}: ListOperatorProps): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(false);

  const [page, setPage] = useState<number>(0);

  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const {
    data,
    get: getOperatorsByQualification,
    error,
  } = useHttpFetch<HttpResponseList<Operator>>(`${__MANAGEMENT_API_URL__}/operatori/qualifiche`, {
    data: {},
  });

  const operators: Array<OperatorClient> = useMemo(() => {
    if (!!error || !data?.payload) {
      return [];
    }
    return data.payload.list.map((operator) => ({
      name: operator.cognomeNome,
      qualification: operator.qualifica?.descrizione || '',
      service: operator.servizio?.descrizioneBreve || '',
      code: operator.key.codiceOperatore,
    }));
  }, [data]);

  useEffect(() => {
    if (qualificationId) {
      getOperatorsByQualification(qualificationId);
    }
  }, [qualificationId]);

  const count: number = useMemo(() => {
    if (!!error || !data?.payload || !data?.payload.totalRows) {
      return 0;
    }
    return data.payload.totalRows;
  }, [error, data?.payload]);

  const auths = useActionAuthorization({
    read: [SystemAuthorizations.OperatoreQuery],
    edit: [SystemAuthorizations.OperatoreUpdate],
  });

  return operators.length > 0 ? (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="panel-operator-content"
        id="panel-operator-header"
        classes={{
          root: 'pmi-flex-row-reverse',
        }}
        expandIcon={<ExpandMore />}
      >
        <Typography>Lista Operatori</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TableOperator
          activeActions={auths}
          page={page}
          isEdit={isEdit}
          rowsPerPage={rowsPerPage}
          ariaLabel="Lista Operatori"
          body={operators}
          head={headCells}
          count={count}
          onChangePage={(newPage) => {
            setPage(newPage || 0);
          }}
          onChangeRowsPerPage={(newRowsPerPage) => {
            setRowsPerPage(newRowsPerPage || 0);
            setPage(0);
          }}
          onDetailPress={(row) => {
            if (onDetailPress) {
              onDetailPress(row.code || '');
            }
          }}
          onEditPress={(row) => {
            if (onEditPress) {
              onEditPress(row.code || '');
            }
          }}
        />
      </AccordionDetails>
    </Accordion>
  ) : (
    <></>
  );
};

ListOperator.displayName = 'List Operator component';

export default ListOperator;
